import React, { useCallback } from 'react';
import {
  Button,
  ChevronRightIcon,
  Fact,
  LedgerHoverCard,
  LedgerRow,
  type LedgerCollapse,
  type LedgerColumn,
} from '@billda/design-system';

type GangMember = {
  userId: string;
  givenName?: string | null;
  familyName?: string | null;
  isLeader: boolean;
  canSeeSplit?: boolean | null;
};

interface GangItem {
  id: string;
  name: string;
  isEqualSplit: boolean;
  members: GangMember[];
}

export interface GangListItemProps {
  gang: GangItem;
  columns: readonly LedgerColumn[];
  collapse: LedgerCollapse;
  onRowClick?: (id: string) => void;
}

const getFullName = (member: GangMember): string => {
  const given = (member.givenName ?? '').trim();
  const family = (member.familyName ?? '').trim();
  return given || family ? `${given} ${family}`.trim() : 'Unknown';
};

export const GangListItem = React.memo(function GangListItem({
  gang,
  columns,
  collapse,
  onRowClick,
}: GangListItemProps) {
  const handleClick = useCallback(() => {
    onRowClick?.(gang.id);
  }, [onRowClick, gang.id]);

  const leaders = gang.members.filter(member => member.isLeader);
  const memberLabel = `${gang.members.length} member${gang.members.length === 1 ? '' : 's'}`;
  const leaderLabel = `${leaders.length} leader${leaders.length === 1 ? '' : 's'}`;
  const leaderNames = leaders.map(getFullName).join(', ') || 'None';
  const splitLabel = gang.isEqualSplit ? 'Equal' : 'Custom';
  const visibilityConfigured = gang.members.some(
    member => member.canSeeSplit != null
  );
  const visibleSplitCount = gang.members.filter(
    member => member.canSeeSplit === true
  ).length;

  return (
    <LedgerRow
      columns={columns}
      collapse={collapse}
      title={gang.name}
      cells={{
        members: memberLabel,
        leaders: leaderLabel,
        split: splitLabel,
      }}
      meta={`${memberLabel} · ${leaderLabel} · ${splitLabel} split`}
      showValues={false}
      hoverCard={
        <LedgerHoverCard
          title={gang.name}
          kind="Gang"
          onOpenDetails={handleClick}
          openDetailsLabel="Open gang details"
        >
          <Fact label="Leaders">{leaderNames}</Fact>
          <Fact label="Members">{gang.members.length}</Fact>
          <Fact label="Split">{`${splitLabel} shares`}</Fact>
          {visibilityConfigured && (
            <Fact label="Visibility">
              {`${visibleSplitCount} of ${gang.members.length} can view split`}
            </Fact>
          )}
        </LedgerHoverCard>
      }
      actions={
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClick}
          aria-label={`Open ${gang.name}`}
          className="size-8"
        >
          <ChevronRightIcon aria-hidden="true" className="size-4" />
        </Button>
      }
      onClick={handleClick}
      aria-label={`Open gang ${gang.name}`}
      data-testid="gang-row"
    />
  );
});
